/**
 * src/i18n/t.js
 *
 * Lookup + interpolation for the i18n foundation. Dictionaries are FLAT
 * objects keyed by dotted string (e.g. 'common.save', 'feedback.reply') —
 * see en.js / ar.js. Flat-keyed rather than nested so key-set-equality
 * (en vs ar) is a trivial Object.keys() diff — see
 * web-admin/scripts/i18n-smoke.mjs.
 */

import { getLang } from './store.js';
import { peekDict } from './dicts.js';

// The dictionaries are LAZY (see dicts.js). They used to be static imports,
// which put both of them — 1363 KB of source — into the entry chunk every
// visitor downloads before first paint. Now only the active language's
// dictionary is fetched, at boot and on toggle, and t() stays synchronous
// because dicts.js guarantees residency before any render runs.
//
// EMPTY is the pre-load / failed-load stand-in. Looking a key up against it
// falls through to the raw key, which renders visibly wrong rather than
// blank — the same worst case the original fallback chain guaranteed.
const EMPTY = Object.freeze(Object.create(null));

/** The active dictionary, or EMPTY if it is somehow not resident yet. */
function activeDict() {
  return peekDict(getLang()) || EMPTY;
}

/**
 * The English dictionary IF it happens to be resident — the belt described in
 * dicts.js, not the mechanism. en/ar key parity is exact and gate-enforced,
 * so this never fires in practice; it exists so that a future parity break
 * degrades to a visible raw key instead of something worse.
 */
function fallbackDict() {
  return peekDict('en') || EMPTY;
}

/**
 * Looks up `key` in the active language dict, falls back to the English
 * dict, then falls back to the raw key string itself — so a missing
 * translation never renders blank or throws; worst case the UI shows the
 * literal key, which is loud enough to notice and fix.
 *
 * @param {string} key      — dotted key, e.g. 'common.save'
 * @param {Object} [params] — {name}-style interpolation values
 * @returns {string}
 */
export function t(key, params) {
  const active = activeDict();
  const en = fallbackDict();
  const raw =
    (Object.prototype.hasOwnProperty.call(active, key) && active[key]) ||
    (Object.prototype.hasOwnProperty.call(en, key) && en[key]) ||
    key;
  return interpolate(raw, params);
}

/** Replaces `{name}`-style placeholders with values from `params`. Leaves an
 * unmatched placeholder (no corresponding param) untouched rather than
 * silently blanking it — a missing param is a caller bug worth noticing. */
function interpolate(str, params) {
  if (!params || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match,
  );
}

// ── CLDR plural lookup ───────────────────────────────────────────────────
// Arabic has SIX plural categories (zero/one/two/few/many/other) — a binary
// singular/plural noun pick renders wrong Arabic for 0, 2, 3-10 and 11-99.
// tn() resolves '<key>.<category>' via Intl.PluralRules, falling back to
// '<key>.other', then the flat '<key>' itself (the legacy binary form), so a
// missing category key can never render blank. Dictionaries stay flat — the
// category is just a dotted suffix, so the en/ar key-parity smoke check
// (i18n-smoke.mjs) covers the plural forms with zero special-casing.

const _pluralRules = {};

function pluralCategory(lang, n) {
  try {
    if (!_pluralRules[lang]) _pluralRules[lang] = new Intl.PluralRules(lang === 'ar' ? 'ar' : 'en');
    return _pluralRules[lang].select(n);
  } catch {
    // Ancient browser without Intl.PluralRules — degrade to the exact
    // pre-tn() binary behavior.
    return n === 1 ? 'one' : 'other';
  }
}

/**
 * Plural-aware lookup: picks the CLDR category for `count` in the active
 * language and resolves `key + '.' + category`, then `key + '.other'`, then
 * the flat `key` (legacy fallback). `{count}` is always available as an
 * interpolation param alongside `params`.
 *
 * @param {string} key      — base dotted key, e.g. 'notifications.rowCount'
 * @param {number} count    — the quantity driving plural selection
 * @param {Object} [params] — extra {name}-style interpolation values
 * @returns {string}
 */
export function tn(key, count, params) {
  const lang = getLang();
  const cat = pluralCategory(lang, count);
  const active = activeDict();
  const en = fallbackDict();
  const merged = { count, ...params };
  for (const k of [`${key}.${cat}`, `${key}.other`, key]) {
    if (Object.prototype.hasOwnProperty.call(active, k)) return interpolate(active[k], merged);
    if (Object.prototype.hasOwnProperty.call(en, k)) return interpolate(en[k], merged);
  }
  return interpolate(key, merged);
}

/**
 * Stamps <html lang/dir> from the current language. Re-exported here (it
 * actually lives in store.js, which owns all document/localStorage access)
 * so app boot can do a single `import { applyDir } from './i18n/t.js'` and
 * call it once before first paint, per the task spec's naming.
 */
export { stampDocument as applyDir } from './store.js';
